/**
 * ErrorBoundary - Catches React component errors and reports them
 * 
 * Wraps the entire app to catch any uncaught rendering errors
 * and display a friendly fallback UI while reporting to monitoring services.
 */

import React, { Component, ReactNode } from 'react';
import { View, StyleSheet, Pressable } from 'react-native';
import * as Sentry from '@sentry/react-native';
import { Text, Button, Theme } from './ui';
import { errorReporting } from '../services/errorReportingService';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to monitoring services
    console.error('🚨 ErrorBoundary caught an error:', error, errorInfo);

    // Report to Sentry with component stack
    Sentry.withScope((scope) => {
      scope.setContext('errorBoundary', {
        componentStack: errorInfo.componentStack,
      });
      Sentry.captureException(error);
    });

    // Also report through our centralized service
    errorReporting.reportError(error, {
      screen: 'ErrorBoundary',
      action: 'component_crash',
      metadata: {
        componentStack: errorInfo.componentStack,
      },
    });
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <View style={styles.container}>
          <View style={styles.content}>
            <Text variant="heading2" align="center" color="error" style={styles.title}>
              Oops! Something went wrong
            </Text>
            
            <Text variant="body1" color="secondary" align="center" style={styles.message}>
              We've been notified and are working on it. Please try again.
            </Text>

            {__DEV__ && this.state.error && (
              <View style={styles.errorDetails}>
                <Text variant="caption" color="error" style={styles.errorMessage}>
                  {this.state.error.message}
                </Text>
              </View>
            )}

            <Button
              title="Try Again"
              onPress={this.resetError}
              style={styles.button}
            />
          </View>
        </View>
      );
    }

    return this.props.children;
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Theme.colors.background,
    padding: Theme.spacing.xl,
  },
  content: {
    maxWidth: 400,
    width: '100%',
    gap: Theme.spacing.lg,
  },
  title: {
    marginBottom: Theme.spacing.sm,
  },
  message: {
    marginBottom: Theme.spacing.md,
  },
  errorDetails: {
    backgroundColor: Theme.colors.surface,
    padding: Theme.spacing.md,
    borderRadius: Theme.borderRadius.md,
    marginTop: Theme.spacing.sm,
  },
  errorMessage: {
    fontFamily: 'monospace',
    fontSize: 12,
  },
  button: {
    marginTop: Theme.spacing.lg,
  },
});

export default ErrorBoundary;

